import { useState, useEffect } from "react";
import { trpc } from "../../trpc";
import SectionTitle from "../../components/SectionTitle";
import MetricSection from "../../components/analytics/MetricSection";
import type { Column } from "../../components/analytics/SortableMetricTable";
import { formatInteger, formatRatio, formatPercentage } from "../../lib/format";
import { Spinner } from "@/components/ui/spinner";
import { Alert } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, AlertCircle } from "lucide-react";
import type { RouterOutputs } from "../../trpc";

type Row = RouterOutputs["metricasAgenteComercial"]["listingsPerMerchant"]["rows"][number];

const leadsPerListingColumns: Column<Row>[] = [
  {
    key: "merchantId",
    header: "Merchant",
    align: "left",
    sticky: true,
    render: (r) => <span className="font-medium text-gray-900">{r.merchantId}</span>,
    sortAccessor: (r) => r.merchantId,
  },
  {
    key: "totalLeads",
    header: "Leads",
    render: (r) => formatInteger(r.totalLeads),
    sortAccessor: (r) => r.totalLeads,
  },
  {
    key: "totalListings",
    header: "Inmuebles",
    render: (r) => formatInteger(r.totalListings),
    sortAccessor: (r) => r.totalListings,
  },
  {
    key: "leadsPerListing",
    header: "Leads / Inmueble",
    render: (r) => (
      <span className="font-semibold text-palomma-secondary">
        {r.leadsPerListing === null ? "—" : formatRatio(r.leadsPerListing)}
      </span>
    ),
    sortAccessor: (r) => r.leadsPerListing,
  },
];

const listingsPerAsesorColumns: Column<Row>[] = [
  {
    key: "merchantId",
    header: "Merchant",
    align: "left",
    sticky: true,
    render: (r) => <span className="font-medium text-gray-900">{r.merchantId}</span>,
    sortAccessor: (r) => r.merchantId,
  },
  {
    key: "totalListings",
    header: "Inmuebles",
    render: (r) => formatInteger(r.totalListings),
    sortAccessor: (r) => r.totalListings,
  },
  {
    key: "activeAsesores",
    header: "Asesores activos",
    render: (r) => formatInteger(r.activeAsesores),
    sortAccessor: (r) => r.activeAsesores,
  },
  {
    key: "listingsPerAsesor",
    header: "Inmuebles / Asesor",
    render: (r) => (
      <span className="font-semibold text-palomma-secondary">
        {r.listingsPerAsesor === null ? "—" : formatRatio(r.listingsPerAsesor)}
      </span>
    ),
    sortAccessor: (r) => r.listingsPerAsesor,
  },
];

const appointmentConversionColumns: Column<Row>[] = [
  {
    key: "merchantId",
    header: "Merchant",
    align: "left",
    sticky: true,
    render: (r) => <span className="font-medium text-gray-900">{r.merchantId}</span>,
    sortAccessor: (r) => r.merchantId,
  },
  {
    key: "totalReachableLeads",
    header: "Leads alcanzables",
    render: (r) => formatInteger(r.totalReachableLeads),
    sortAccessor: (r) => r.totalReachableLeads,
  },
  {
    key: "totalAppointments",
    header: "Citas",
    render: (r) => formatInteger(r.totalAppointments),
    sortAccessor: (r) => r.totalAppointments,
  },
  {
    key: "appointmentConversionRate",
    header: "Conversión a citas",
    render: (r) => (
      <span className="font-semibold text-palomma-secondary">
        {r.appointmentConversionRate === null
          ? "—"
          : formatPercentage(r.appointmentConversionRate)}
      </span>
    ),
    sortAccessor: (r) => r.appointmentConversionRate,
  },
];

const transferConversionColumns: Column<Row>[] = [
  {
    key: "merchantId",
    header: "Merchant",
    align: "left",
    sticky: true,
    render: (r) => <span className="font-medium text-gray-900">{r.merchantId}</span>,
    sortAccessor: (r) => r.merchantId,
  },
  {
    key: "totalReachableLeads",
    header: "Leads alcanzables",
    render: (r) => formatInteger(r.totalReachableLeads),
    sortAccessor: (r) => r.totalReachableLeads,
  },
  {
    key: "totalTransferredLeads",
    header: "Transferidos",
    render: (r) => formatInteger(r.totalTransferredLeads),
    sortAccessor: (r) => r.totalTransferredLeads,
  },
  {
    key: "transferRate",
    header: "Tasa transferencia",
    render: (r) => (
      <span className="font-semibold text-palomma-secondary">
        {r.transferRate === null ? "—" : formatPercentage(r.transferRate)}
      </span>
    ),
    sortAccessor: (r) => r.transferRate,
  },
];

const MetricasAgenteComercialContainer = () => {
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDayOfMonth.toISOString().split("T")[0]);
    setEndDate(today.toISOString().split("T")[0]);
  }, []);

  const query = trpc.metricasAgenteComercial.listingsPerMerchant.useQuery(
    { startDate, endDate },
    { enabled: !!startDate && !!endDate }
  );

  const rows = query.data?.rows ?? [];
  const leadsGlobals = query.data?.globals.leadsPerListing;
  const asesoresGlobals = query.data?.globals.listingsPerAsesor;
  const appointmentGlobals = query.data?.globals.appointmentConversion;
  const transferGlobals = query.data?.globals.transferConversion;

  return (
    <div className="p-6 mx-auto w-full">
      <div className="flex flex-col gap-8 w-full">
        <SectionTitle
          title="Métricas Agente Comercial — Rentals"
          description="Métricas cross-merchant para rentals activos. Pipeline default (inbound sales)."
          actions={
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-100 text-purple-800 border border-purple-200 text-xs font-semibold">
              <Home className="h-3.5 w-3.5" />
              Rentals
            </div>
          }
        />

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="startDate" className="text-xs">
              Fecha de inicio
            </Label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="endDate" className="text-xs">
              Fecha de fin
            </Label>
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-44"
            />
          </div>
        </div>

        {query.isLoading && (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        )}

        {query.error && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <span className="ml-2">Error: {query.error.message}</span>
          </Alert>
        )}

        {leadsGlobals && asesoresGlobals && appointmentGlobals && transferGlobals && (
          <>
            <MetricSection
              title="Leads por inmueble"
              stats={[
                {
                  title: "Promedio simple",
                  value: formatRatio(leadsGlobals.promedioSimple),
                  description:
                    "Promedio del ratio leads/inmueble entre todos los merchants. Cada merchant cuenta igual.",
                  formula: "avg(leads_i / listings_i)",
                },
                {
                  title: "Promedio ponderado por leads",
                  value: formatRatio(leadsGlobals.promedioPonderadoPorLeads),
                  description:
                    "Ratio ponderado por el número de leads de cada merchant. Merchants con más leads pesan más.",
                  formula: "Σ(ratio_i × leads_i) / Σ(leads_i)",
                },
                {
                  title: "Ratio agregado",
                  value: formatRatio(leadsGlobals.ratioAgregado),
                  description:
                    "Suma total de leads dividida por suma total de inmuebles. Tratamiento del portafolio como un único merchant.",
                  formula: "Σ(leads) / Σ(listings)",
                },
              ]}
              summary={
                <>
                  {leadsGlobals.merchantsIncluidos} merchants incluidos en globals (con inmuebles &gt; 0)
                  {leadsGlobals.merchantsExcluidos > 0 &&
                    ` · ${leadsGlobals.merchantsExcluidos} excluidos por no tener snapshot en el rango`}
                  {" · "}
                  Total: {formatInteger(leadsGlobals.totalLeads)} leads / {formatInteger(leadsGlobals.totalListings)} inmuebles
                </>
              }
              columns={leadsPerListingColumns}
              rows={rows}
              defaultSort={{ columnKey: "totalLeads", direction: "desc" }}
              emptyMessage="No hay merchants rentals con datos en el rango seleccionado."
            />

            <MetricSection
              title="Inmuebles por asesor"
              stats={[
                {
                  title: "Promedio simple",
                  value: formatRatio(asesoresGlobals.promedioSimple),
                  description:
                    "Promedio del ratio inmuebles/asesor entre todos los merchants. Cada merchant cuenta igual.",
                  formula: "avg(listings_i / asesores_i)",
                },
                {
                  title: "Promedio ponderado por inmuebles",
                  value: formatRatio(asesoresGlobals.promedioPonderadoPorListings),
                  description:
                    "Ratio ponderado por el número de inmuebles de cada merchant. Merchants con más inventario pesan más.",
                  formula: "Σ(ratio_i × listings_i) / Σ(listings_i)",
                },
                {
                  title: "Ratio agregado",
                  value: formatRatio(asesoresGlobals.ratioAgregado),
                  description:
                    "Suma total de inmuebles dividida por suma total de asesores activos.",
                  formula: "Σ(listings) / Σ(asesores)",
                },
              ]}
              summary={
                <>
                  {asesoresGlobals.merchantsIncluidos} merchants incluidos en globals (con asesores activos &gt; 0)
                  {asesoresGlobals.merchantsExcluidos > 0 &&
                    ` · ${asesoresGlobals.merchantsExcluidos} excluidos por no tener asesores activos`}
                  {" · "}
                  Total: {formatInteger(asesoresGlobals.totalListings)} inmuebles / {formatInteger(asesoresGlobals.totalAsesores)} asesores
                </>
              }
              columns={listingsPerAsesorColumns}
              rows={rows}
              defaultSort={{ columnKey: "totalListings", direction: "desc" }}
              emptyMessage="No hay merchants rentals con asesores en el rango seleccionado."
            />

            <MetricSection
              title="Conversión a citas"
              description="Porcentaje de leads alcanzables que agendaron al menos una cita en el rango. Leads alcanzables = leads del pipeline default con al menos un mensaje entregado/leído del ai_agent o human_agent."
              stats={[
                {
                  title: "Promedio simple",
                  value: formatPercentage(appointmentGlobals.promedioSimple),
                  description:
                    "Promedio del ratio citas / leads alcanzables entre todos los merchants. Cada merchant cuenta igual.",
                  formula: "avg(citas_i / leads_alcanzables_i)",
                },
                {
                  title: "Promedio ponderado por leads alcanzables",
                  value: formatPercentage(
                    appointmentGlobals.promedioPonderadoPorReachableLeads
                  ),
                  description:
                    "Ratio ponderado por el número de leads alcanzables de cada merchant. Merchants con más volumen pesan más.",
                  formula:
                    "Σ(ratio_i × leads_alcanzables_i) / Σ(leads_alcanzables_i)",
                },
                {
                  title: "Ratio agregado",
                  value: formatPercentage(appointmentGlobals.ratioAgregado),
                  description:
                    "Suma total de citas dividida por suma total de leads alcanzables. Tratamiento del portafolio como un único merchant.",
                  formula: "Σ(citas) / Σ(leads_alcanzables)",
                },
              ]}
              summary={
                <>
                  {appointmentGlobals.merchantsIncluidos} merchants incluidos en globals (con citas &gt; 0)
                  {appointmentGlobals.merchantsExcluidos > 0 &&
                    ` · ${appointmentGlobals.merchantsExcluidos} excluidos por no tener citas en el rango`}
                  {" · "}
                  Total: {formatInteger(appointmentGlobals.totalAppointments)} citas / {formatInteger(appointmentGlobals.totalReachableLeads)} leads alcanzables
                </>
              }
              columns={appointmentConversionColumns}
              rows={rows}
              defaultSort={{ columnKey: "totalReachableLeads", direction: "desc" }}
              emptyMessage="No hay merchants rentals con datos en el rango seleccionado."
            />

            <MetricSection
              title="Conversión a transferencia"
              description="Porcentaje de leads alcanzables cuya conversación fue transferida a un humano (transferReason ≠ null). Mismo denominador que Conversión a citas."
              stats={[
                {
                  title: "Promedio simple",
                  value: formatPercentage(transferGlobals.promedioSimple),
                  description:
                    "Promedio del ratio transferidos / leads alcanzables entre todos los merchants con leads alcanzables. Cada merchant cuenta igual.",
                  formula: "avg(transferidos_i / leads_alcanzables_i)",
                },
                {
                  title: "Promedio ponderado por leads alcanzables",
                  value: formatPercentage(
                    transferGlobals.promedioPonderadoPorReachableLeads
                  ),
                  description:
                    "Ratio ponderado por el número de leads alcanzables de cada merchant. Merchants con más volumen pesan más.",
                  formula:
                    "Σ(ratio_i × leads_alcanzables_i) / Σ(leads_alcanzables_i)",
                },
                {
                  title: "Ratio agregado",
                  value: formatPercentage(transferGlobals.ratioAgregado),
                  description:
                    "Suma total de transferencias dividida por suma total de leads alcanzables.",
                  formula: "Σ(transferidos) / Σ(leads_alcanzables)",
                },
              ]}
              summary={
                <>
                  {transferGlobals.merchantsIncluidos} merchants incluidos en globals (con leads alcanzables &gt; 0)
                  {transferGlobals.merchantsExcluidos > 0 &&
                    ` · ${transferGlobals.merchantsExcluidos} excluidos por no tener leads alcanzables en el rango`}
                  {" · "}
                  Total: {formatInteger(transferGlobals.totalTransferredLeads)} transferidos / {formatInteger(transferGlobals.totalReachableLeads)} leads alcanzables
                </>
              }
              columns={transferConversionColumns}
              rows={rows}
              defaultSort={{ columnKey: "totalReachableLeads", direction: "desc" }}
              emptyMessage="No hay merchants rentals con leads alcanzables en el rango seleccionado."
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MetricasAgenteComercialContainer;
